import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Package, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const region = (r) => (r?.length === 1 && r[0] === "IN" ? "India" : r?.includes("00") || r?.length > 3 ? "Worldwide" : (r || []).join(", "));

const LastImport = ({ li }) => {
  if (!li) return <span className="text-slate-400">not imported yet</span>;
  if (li.running) return <span className="text-amber-700 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> importing…</span>;
  if (li.error) return <span className="text-red-600">{li.error}</span>;
  return <span className="text-slate-600">{li.created} new, {li.updated} updated deals · {new Date(li.finished_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>;
};

const FeedRow = ({ f, busy, act }) => (
  <tr className="border-t border-slate-100" data-testid={`admitad-feed-row-${f.feed_id}`}>
    <td className="py-2 pr-3"><p className="font-semibold">{f.campaign}</p><p className="text-[11px] text-slate-500">{f.feed_name} · {region(f.regions)}</p></td>
    <td className="py-2 pr-3 text-xs" data-testid={`admitad-feed-status-${f.feed_id}`}>{f.connected ? <LastImport li={f.last_import} /> : <span className="text-slate-400">not connected</span>}</td>
    <td className="py-2 text-right whitespace-nowrap">
      {f.connected ? (<>
        <button disabled={busy || f.last_import?.running} onClick={() => act("import", f)} className="text-xs font-semibold px-2.5 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-40 mr-1" data-testid={`admitad-feed-import-${f.feed_id}`}>Import now</button>
        <button disabled={busy} onClick={() => act("disconnect", f)} className="text-xs font-semibold px-2.5 py-1.5 rounded-md border border-slate-300" data-testid={`admitad-feed-disconnect-${f.feed_id}`}>Disconnect</button>
      </>) : (
        <button disabled={busy} onClick={() => act("connect", f)} className="text-xs font-semibold px-2.5 py-1.5 rounded-md bg-emerald-600 text-white" data-testid={`admitad-feed-connect-${f.feed_id}`}>Connect</button>
      )}
    </td>
  </tr>
);

export const AdmitadFeedsCard = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data, isFetching, error } = useQuery({
    queryKey: ["admitad-feeds"], enabled: open, retry: false,
    queryFn: () => api.get("/admin/admitad/product-feeds").then((r) => r.data.feeds),
    refetchInterval: (q) => (q.state.data || []).some((f) => f.last_import?.running) ? 4000 : false,
  });
  const act = async (kind, f) => {
    setBusy(true);
    try {
      if (kind === "import") qc.setQueryData(["admitad-feeds"], (old) => (old || []).map((x) => (x.feed_id === f.feed_id ? { ...x, last_import: { running: true } } : x)));
      if (kind === "connect") await api.post("/admin/admitad/product-feeds/connect", { feed_ids: [f.feed_id] });
      else await api.post(`/admin/admitad/product-feeds/${f.feed_id}/${kind}`);
      toast.success(kind === "import" ? "Import started - products with ₹ prices go into Deals" : kind === "connect" ? "Connected - imports run automatically every day" : "Disconnected");
      qc.invalidateQueries({ queryKey: ["admitad-feeds"] });
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="admitad-feeds-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-bold flex items-center gap-2"><Package size={16} /> Admitad product feeds</p>
          <p className="text-xs text-slate-500">Free product lists from stores you've joined on Admitad. Products go into your Deals section; only items priced in ₹ and in stock are imported, and good discounts are published automatically.</p>
        </div>
        <button onClick={() => setOpen(!open)} className="text-sm font-semibold px-3 py-2 rounded-lg border border-slate-300 shrink-0" data-testid="admitad-feeds-toggle">{open ? "Hide" : "Show feeds"}</button>
      </div>
      {open && (isFetching && !data ? <p className="mt-3 text-sm text-slate-500">Loading from Admitad…</p>
        : error ? <p className="mt-3 text-sm text-red-600" data-testid="admitad-feeds-error">{errMsg(error)}</p>
        : <table className="w-full text-sm mt-3"><tbody>{(data || []).map((f) => <FeedRow key={f.feed_id} f={f} busy={busy} act={act} />)}</tbody></table>)}
    </div>
  );
};
